import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class ProductSku extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    
    // Check initial state - if "Please Select" is active, keep default SKU
    this.#checkInitialState();
    
    // Listen for variant updates
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
    
    // Also listen for variant changes to check if we should show default SKU
    closestSection.addEventListener('variant:selected', this.#onVariantSelected);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
    closestSection.removeEventListener('variant:selected', this.#onVariantSelected);
  }

  /**
   * Checks if variant picker has "Please Select" active and shows default SKU if so.
   */
  #checkInitialState() {
    const variantPicker = this.closest('main')?.querySelector('variant-picker');
    if (!variantPicker) return;

    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(variantPicker.querySelectorAll('select.variant-option__select')));
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (Array.from(variantPicker.querySelectorAll('fieldset[ref="fieldsets[]"]')));
    
    // If no variant options, product has only default variant - show variant SKU
    if (selects.length === 0 && fieldsets.length === 0) {
      return; // Let the Liquid template handle this
    }
    
    // Check if any dropdown has "Please Select" (empty value) selected
    const hasUnselectedDropdown = selects.some(select => !select.value || select.value === '');
    
    // Check if any fieldset has no checked input
    const hasUnselectedFieldset = fieldsets.some(fieldset => {
      const checkedInput = fieldset.querySelector('input:checked');
      return !checkedInput;
    });

    // If "Please Select" is active, show default SKU
    if (hasUnselectedDropdown || hasUnselectedFieldset) {
      this.#showDefaultSku();
    }
  }

  /**
   * Shows the default SKU from data attribute.
   */
  #showDefaultSku() {
    const defaultSku = this.dataset.defaultSku;
    if (!defaultSku) return;
    
    const skuSpan = this.querySelector('[ref="skuValue"]');
    if (skuSpan) {
      skuSpan.textContent = defaultSku;
    }
  }

  /**
   * Handles variant selection - checks if we should show default SKU or variant SKU.
   */
  #onVariantSelected = () => {
    // Small delay to let the variant picker update
    setTimeout(() => {
      this.#checkInitialState();
    }, 50);
  };

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  updateSku = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // At this point, all options are selected and VariantUpdateEvent contains the new variant
    const variant = event.detail.resource;
    const variantSku = variant && typeof variant.sku === 'string' ? variant.sku : '';

    const skuSpan = this.querySelector('[ref="skuValue"]');
    if (!skuSpan) return;

    if (variantSku) {
      // Show the selected variant's SKU
      skuSpan.textContent = variantSku;
    } else {
      // Fallback to default SKU if variant has no SKU
      this.#showDefaultSku();
    }
  };
}

if (!customElements.get('product-sku')) {
  customElements.define('product-sku', ProductSku);
}

